import { apiFetch } from '../authentication/authUtils';
import { getResponseErrorTitle } from '../shared/apiUtils';

export interface Connection {
    appId: string;
    name: string;
    connectorId: string;
    identityProviderId: string;
    identityProviderName: string;
    clientId: string;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CreatedConnection extends Connection {
    clientSecret: string;
}

export interface SaveConnectionRequest {
    name: string;
    identityProviderId: string | null;
    connectorId: string;
    options: Record<string, string | string[] | boolean | number | null>;
    enabled: boolean;
    accountOwned: boolean;
}

interface PaginatedResponse<T> {
    results: T[];
    total: number;
    pageSize: number;
    page: number;
}

export async function getAllConnections(): Promise<Connection[]> {
    const response = await apiFetch('/fabric-oauth-app?pageSize=100');

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    const data = (await response.json()) as PaginatedResponse<Connection>;
    return data.results;
}

export async function getConnections(connectorId: string): Promise<Connection[]> {
    const data = await getAllConnections();
    return data.filter((c) => c.connectorId === connectorId);
}

export async function getConnection(id: string): Promise<Connection> {
    const response = await apiFetch(`/fabric-oauth-app/${id}`);

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    return response.json() as Promise<Connection>;
}

export async function createConnection(request: SaveConnectionRequest): Promise<CreatedConnection> {
    const response = await apiFetch('/fabric-oauth-app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    return response.json() as Promise<CreatedConnection>;
}

export async function deleteConnection(id: string): Promise<void> {
    const response = await apiFetch(`/fabric-oauth-app/${id}`, { method: 'DELETE' });

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }
}

export async function updateConnection(
    id: string,
    request: SaveConnectionRequest,
): Promise<Connection> {
    const response = await apiFetch(`/fabric-oauth-app/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        throw new Error(await getResponseErrorTitle(response));
    }

    return response.json() as Promise<Connection>;
}
